/** Turning an API failure into something a person can act on. */

import { ApiError } from '@/api/client'

const NETWORK_MESSAGE = 'Could not reach the server. Check your connection and try again.'
const UNEXPECTED_MESSAGE = 'Something went wrong. Please try again.'

/**
 * DRF's `ErrorDetail` is a `str` subclass, so a serializer that raises
 * `ValidationError({"detail": ...})` reaches the client stringified as a Python
 * repr rather than as prose. That path covers the most common failure there is —
 * a wrong password — so unwrap it instead of showing the user
 * `[ErrorDetail(string='Incorrect email or password.', code='invalid')]`.
 */
const ERROR_DETAIL_REPR = /ErrorDetail\(string=(['"])(.*?)\1\s*,/g

export function cleanDetail(detail: string): string {
  const unwrapped = [...detail.matchAll(ERROR_DETAIL_REPR)].map((match) => match[2])
  return unwrapped.length > 0 ? unwrapped.join(' ') : detail
}

/** The message to render under a specific input, if the server blamed that input. */
export function fieldErrorOf(error: unknown, name: string): string | undefined {
  if (!(error instanceof ApiError)) return undefined
  const message = error.fieldError(name)
  return message ? cleanDetail(message) : undefined
}

/**
 * The message for the banner above the form.
 *
 * `handledFields` are the inputs that render their own inline error. Anything the
 * server blamed that the form has no input for still has to surface somewhere, so
 * it is folded into the banner.
 */
export function generalErrorOf(
  error: unknown,
  handledFields: readonly string[] = [],
): string | undefined {
  if (!error) return undefined

  if (!(error instanceof ApiError)) {
    // fetch() rejects with a TypeError when the request never reached the server.
    return error instanceof TypeError ? NETWORK_MESSAGE : UNEXPECTED_MESSAGE
  }

  const orphaned = Object.entries(error.fields)
    .filter(([name]) => !handledFields.includes(name))
    .map(([, messages]) => cleanDetail(messages[0] ?? ''))
    .filter((message) => message.length > 0)

  if (orphaned.length > 0) return orphaned.join(' ')

  // Every problem is already flagged beside its own input; repeating the API's
  // "check the highlighted fields" summary on top of them adds nothing.
  if (Object.keys(error.fields).length > 0) return undefined

  return cleanDetail(error.message)
}
